import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { MachineDto } from 'src/app/shared/dto/machine.dto';
import { PauseReasonsResponseDto } from 'src/app/shared/dto/pagination/pause-reasons-response.dto';
import { PauseReasonDto } from 'src/app/shared/dto/pause-reason.dto';
import { BaseService } from 'src/app/shared/services/base.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { formatClientRoom, Client, RoomNames, formatClientRoomId } from 'src/app/shared/sockets/enums/rooms';

@Injectable({
    providedIn: 'root',
})
export class PauseReasonsService extends BaseService {
    public pauseReasonsByMachine$: Observable<PauseReasonDto[]>;
    public pauseReasonById$: Observable<PauseReasonDto | { deleted:boolean } | null>;
    public pauseReasonsPaginated$: Observable<PauseReasonsResponse>;

    private pauseReasonsSubjectByMachine = new BehaviorSubject<PauseReasonDto[]>([]);
    private messageSubscriptionByMachine: Subscription | undefined;

    private pauseReasonsPaginatedSubject = new BehaviorSubject<PauseReasonsResponse>(new PauseReasonsResponse);
    private messagePaginatedSubscription: Subscription | undefined;

    private pauseReasonSubjectById = new Subject<PauseReasonDto | { deleted:boolean } | null>();
    private messageSubscriptionById: Subscription | undefined;
    private deleteSubscriptionById: Subscription | undefined;

    private pageNumber: number = 1;
    private pageSize: number = 20;
    private filterText: string = '';

    private readonly socketService = inject(SocketService);

    constructor(httpClient: HttpClient) {
        super(httpClient, 'pause-reasons');

        this.pauseReasonsByMachine$ = this.pauseReasonsSubjectByMachine.asObservable();
        this.pauseReasonById$ = this.pauseReasonSubjectById.asObservable();
        this.pauseReasonsPaginated$ = this.pauseReasonsPaginatedSubject.asObservable();
    }

    refreshVariables(pageSize: number, pageNumber: number, filterText: string): void {
        this.pageSize = pageSize;
        this.pageNumber = pageNumber;
        this.filterText = filterText;
    }

    async setupWebSocketByMachine(machineId:number): Promise<void> {
        try {
            const pauseReasons = await this.getByMachineId(machineId);
            this.pauseReasonsSubjectByMachine.next(pauseReasons);

            this.socketService.conectSocketRoom(formatClientRoomId(Client.DEFAULT_CLIENT, RoomNames.PAUSE_REASONS_ROOM_BY_MACHINE, machineId));
            this.messageSubscriptionByMachine = this.socketService.getNotifications(Messages.PAUSE_REASONS_CHANGED).subscribe(async () => {
                const pauseReasons = await this.getByMachineId(machineId);
                this.pauseReasonsSubjectByMachine.next(pauseReasons);
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Motivos de pausas. Error: ${e.message}`);
        }
    }

    disconnectWebSocketByMachine(machineId:number): void {
        if(this.messageSubscriptionByMachine){
            this.messageSubscriptionByMachine.unsubscribe();
        }
        this.socketService.disconnectSocketRoom(formatClientRoomId(Client.DEFAULT_CLIENT, RoomNames.PAUSE_REASONS_ROOM_BY_MACHINE, machineId));
    }

    async setupWebSocketPaginated(pageSize: number, pageNumber: number): Promise<void> {
        this.pageSize = pageSize;
        this.pageNumber = pageNumber;

        try {
            const response = await this.getPaginatedPauseReasons(this.pageSize, this.pageNumber, this.filterText);
            this.pauseReasonsPaginatedSubject.next({ pauseReasons: response.pauseReasons, totalPauseReasons: response.total });

            this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PAUSE_REASONS_ROOM));
            this.messagePaginatedSubscription = this.socketService.getNotifications(Messages.PAUSE_REASONS_CHANGED).subscribe(async () => {
                const response = await this.getPaginatedPauseReasons(this.pageSize, this.pageNumber, this.filterText);
                this.pauseReasonsPaginatedSubject.next({ pauseReasons: response.pauseReasons, totalPauseReasons: response.total });
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Motivos de pausas paginados. Error: ${e.message}`);
        }
    }

    disconnectWebSocketPaginated(): void {
        this.messagePaginatedSubscription!.unsubscribe();
        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PAUSE_REASONS_ROOM));
    }

    async setupWebSocketById(pauseReasonId: number): Promise<void> {
        try {
            this.pauseReasonSubjectById = new Subject<PauseReasonDto | { deleted:boolean } | null>();
            this.pauseReasonById$ = this.pauseReasonSubjectById.asObservable();

            this.socketService.conectSocketRoom(formatClientRoomId(Client.DEFAULT_CLIENT, RoomNames.PAUSE_REASONS_ROOM, pauseReasonId));

            this.messageSubscriptionById = this.socketService.getNotifications(Messages.PAUSE_REASON_CHANGED).subscribe(async (pauseReason : PauseReasonDto) => {
                const pauseReasonDto = Object.assign(new PauseReasonDto(), pauseReason);
                if (pauseReason.machines) {
                    pauseReasonDto.machines = pauseReason.machines.map(m => Object.assign(new MachineDto(), m));
                }
                this.pauseReasonSubjectById.next(pauseReasonDto);
            });

            this.deleteSubscriptionById = this.socketService.getNotifications(Messages.PAUSE_REASON_DELETED).subscribe(async (pauseReason : PauseReasonDto) => {
                this.pauseReasonSubjectById.next({deleted: true});
            });
        } catch (e: any) {
            throw new Error(`Error al establecer conexión con el socket de Motivo de pausa con ID ${pauseReasonId}. Error: ${e.message}`);
        }
    }

    disconnectWebSocketById(pauseReasonId: number): void {
        this.messageSubscriptionById?.unsubscribe();
        this.deleteSubscriptionById?.unsubscribe();

        this.pauseReasonSubjectById?.complete();

        this.socketService.disconnectSocketRoom(formatClientRoomId(Client.DEFAULT_CLIENT, RoomNames.PAUSE_REASONS_ROOM, pauseReasonId));
    }

    async getPaginatedPauseReasons(pageSize: number, pageNumber: number, filterText: string): Promise<PauseReasonsResponseDto> {
        try {
            let url: string = `${this.url}/findAll?pageSize=${pageSize}&pageNumber=${pageNumber}`;

            if (filterText && filterText != '') {
                url = `${url}&filterText=${filterText}`;
            }

            const request = this.httpClient.get<PauseReasonsResponseDto>(url);

            const pauseReasonsResponse: PauseReasonsResponseDto = await lastValueFrom(request);

            return pauseReasonsResponse;
        } catch (e: any) {
            throw new Error(`No se ha encontrado motivos de pausa. Error: ${e.message}`);
        }
    }

    async getByMachineId(machineId:number): Promise<PauseReasonDto[]> {
        try {
            const url: string = `${this.url}/machine/${machineId}`;

            const request = this.httpClient.get<PauseReasonDto[]>(url);

            const pauseReasons: PauseReasonDto[] = await lastValueFrom(request);

            return pauseReasons;
        } catch (e: any) {
            throw new Error(`No se ha encontrado motivos de pausa. Error: ${e.message}`);
        }
    }

}

export class PauseReasonsResponse {
    pauseReasons!: PauseReasonDto[];
    totalPauseReasons!: number;
}
